import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./modalInventoryLookup.css";

const toInt = (v) => {
  const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

const siteLabel = (site) => (String(site) === "3100" ? "CDMX" : "MTY");

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function ModalInventoryLookup({ isOpen, onClose }) {
  const [site, setSite] = useState("3000");

  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState("");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const [selectedKey, setSelectedKey] = useState("");
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownError, setBreakdownError] = useState("");

  // Cache de QAD por product_id (se llena "on demand" o por carga masiva)
  const [stockByPid, setStockByPid] = useState({}); // { [product_id]: number }
  const [loadingQad, setLoadingQad] = useState(false);
  const [qadError, setQadError] = useState("");

  // Paginación simple para no renderizar miles de filas
  const PAGE_SIZE = 200;
  const [page, setPage] = useState(1);

  const pillClass = (n) => {
    if (n > 0) return "MIL-pillPos";
    if (n < 0) return "MIL-pillNeg";
    return "MIL-pillZero";
  };

  const rowKey = (r, idx) =>
    `${r.product_id || ""}__${r.color_label || ""}__${r.size_label || ""}__${idx}`;

  const resetResults = () => {
    setRows([]);
    setError("");
    setSelectedKey("");
    setBreakdown(null);
    setBreakdownError("");
    setBreakdownLoading(false);
    setStockByPid({});
    setLoadingQad(false);
    setQadError("");
    setPage(1);
  };

  const loadBrands = async (siteValue) => {
    try {
      setError("");
      setBrands([]);
      setBrand("");
      resetResults();

      const resp = await axios.get("/api/inventory/brands", {
        params: { site: siteValue },
        headers: { "Cache-Control": "no-cache" },
        timeout: 30000,
      });

      const payload = resp?.data || {};
      const list = Array.isArray(payload?.brands)
        ? payload.brands
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      const normalized = list
        .map((x) => (typeof x === "string" ? x : x?.brand_name))
        .map((s) => String(s || "").trim())
        .filter(Boolean);

      setBrands(normalized);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.message ||
        "Error cargando marcas.";
      setError(msg);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadBrands(site);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, site]);

  // Helpers QAD
  const fetchSystemStock = async (productId) => {
    const pid = String(productId || "").trim();
    if (!pid) return null;

    // Si ya lo tenemos, no repitas llamada
    if (Object.prototype.hasOwnProperty.call(stockByPid, pid)) {
      return stockByPid[pid];
    }

    const resp = await axios.get("/api/inventory/system-stock", {
      params: { site, product_id: pid },
      headers: { "Cache-Control": "no-cache" },
      timeout: 30000,
    });

    const st = resp?.data?.stock;
    if (typeof st === "number" && Number.isFinite(st)) return Math.trunc(st);
    if (typeof st === "string" && String(st).trim() !== "" && Number.isFinite(Number(st))) {
      return Math.trunc(Number(st));
    }
    return null;
  };

  const ensureStockCached = async (productId) => {
    const pid = String(productId || "").trim();
    if (!pid) return;
    if (Object.prototype.hasOwnProperty.call(stockByPid, pid)) return;

    try {
      const st = await fetchSystemStock(pid);
      setStockByPid((prev) => {
        if (Object.prototype.hasOwnProperty.call(prev, pid)) return prev;
        return { ...prev, [pid]: st === null ? null : st };
      });
    } catch {
      // No rompas UI si falla QAD
      setStockByPid((prev) => {
        if (Object.prototype.hasOwnProperty.call(prev, pid)) return prev;
        return { ...prev, [pid]: null };
      });
    }
  };

  const handleSearch = async () => {
    const b = String(brand || "").trim();
    if (!b) {
      setError("Selecciona una marca.");
      setRows([]);
      return;
    }

    setLoading(true);
    setError("");
    setRows([]);
    setSelectedKey("");
    setBreakdown(null);
    setBreakdownError("");
    setStockByPid({});
    setQadError("");
    setPage(1);

    try {
      // IMPORTANTE: include_stock=0 para evitar timeouts (QAD se carga on-demand)
      const resp = await axios.get("/api/inventory/brand-summary", {
        params: {
          site,
          brand_name: b,
          include_stock: "0",
        },
        headers: { "Cache-Control": "no-cache" },
        timeout: 30000,
      });

      const payload = resp?.data || {};
      const data = Array.isArray(payload?.data) ? payload.data : [];

      if (!payload?.ok) {
        setError(payload?.message || "No se pudo consultar el resumen por marca.");
        setRows([]);
      } else {
        setRows(data);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.message ||
        "Error consultando.";
      setError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Carga masiva de QAD (por lotes) para filas visibles
  const handleLoadQadVisible = async () => {
    if (!rows.length) return;

    setLoadingQad(true);
    setQadError("");

    try {
      const visible = rows.slice(0, page * PAGE_SIZE);
      const pids = Array.from(
        new Set(visible.map((r) => String(r.product_id || "").trim()).filter(Boolean))
      );

      const missing = pids.filter((pid) => !Object.prototype.hasOwnProperty.call(stockByPid, pid));
      if (!missing.length) return;

      // Lotes pequeños para no saturar ni el backend ni QAD
      const BATCH = 8;

      for (let i = 0; i < missing.length; i += BATCH) {
        const batch = missing.slice(i, i + BATCH);

        const results = await Promise.all(
          batch.map(async (pid) => {
            try {
              const st = await fetchSystemStock(pid);
              return [pid, st === null ? null : st];
            } catch {
              return [pid, null];
            }
          })
        );

        setStockByPid((prev) => {
          const next = { ...prev };
          for (const [pid, st] of results) {
            if (!Object.prototype.hasOwnProperty.call(next, pid)) next[pid] = st;
          }
          return next;
        });

        // Micro pausa para evitar ráfagas
        await sleep(80);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.message ||
        "Error cargando QAD.";
      setQadError(msg);
    } finally {
      setLoadingQad(false);
    }
  };

  const openBreakdown = async (r, idx) => {
    const k = rowKey(r, idx);
    setSelectedKey(k);
    setBreakdown(null);
    setBreakdownError("");
    setBreakdownLoading(true);

    // Además: intenta cachear QAD de ese product_id (on-demand)
    if (r?.product_id) ensureStockCached(r.product_id);

    try {
      const resp = await axios.get("/api/inventory/article-breakdown", {
        params: {
          site,
          id: r.product_id,
          like: "0",
          product_id_exact: r.product_id,
          color_label: r.color_label,
          size_label: r.size_label,
          include_stock: "1", // aquí sí vale la pena
        },
        headers: { "Cache-Control": "no-cache" },
        timeout: 30000,
      });

      const payload = resp?.data || {};
      if (!payload?.ok) {
        setBreakdownError(payload?.message || "No se pudo generar el desglose.");
        setBreakdown(null);
      } else {
        setBreakdown(payload);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.message ||
        "Error generando desglose.";
      setBreakdownError(msg);
      setBreakdown(null);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const visibleRows = useMemo(() => rows.slice(0, page * PAGE_SIZE), [rows, page]);

  // Totales: solo mostramos Total QAD y Diferencia global cuando ya tenemos QAD para TODAS las filas visibles
  const totals = useMemo(() => {
    const totalCounted = visibleRows.reduce((a, r) => a + toInt(r.counted_qty), 0);

    const pids = visibleRows
      .map((r) => String(r.product_id || "").trim())
      .filter(Boolean);

    const uniquePids = Array.from(new Set(pids));
    const hasAllStock =
      uniquePids.length > 0 &&
      uniquePids.every((pid) => Object.prototype.hasOwnProperty.call(stockByPid, pid));

    if (!hasAllStock) {
      return { totalCounted, totalQad: null, diff: null, hasAllStock: false };
    }

    // Nota: aquí totalQad suma por fila, pero QAD es por product_id; si hay repetidos por color/talla
    // puede no representar un total perfecto. Por eso lo limitamos a filas visibles y lo mostramos como referencia.
    // Si quieres un total exacto, se requiere endpoint backend de "total QAD por marca".
    const totalQad = visibleRows.reduce((a, r) => {
      const pid = String(r.product_id || "").trim();
      const st = pid ? stockByPid[pid] : null;
      return a + toInt(st);
    }, 0);

    const diff = totalCounted - totalQad;
    return { totalCounted, totalQad, diff, hasAllStock: true };
  }, [visibleRows, stockByPid]);

  if (!isOpen) return null;

  return (
    <div
      className="MIL-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="MIL-modal">
        <div className="MIL-header">
          <div className="MIL-title">Consulta de Inventario (Conteo vs QAD)</div>
          <button className="MIL-closeBtn" onClick={() => onClose?.()}>
            CERRAR
          </button>
        </div>

        <div className="MIL-body">
          <div className="MIL-grid MIL-gridBrand">
            <div className="MIL-field">
              <label>Sucursal</label>
              <select
                className="MIL-select browser-default"
                value={site}
                onChange={(e) => setSite(e.target.value)}
              >
                <option value="3000">MTY</option>
                <option value="3100">CDMX</option>
              </select>
            </div>

            <div className="MIL-field">
              <label>Marca (contada)</label>
              <select
                className="MIL-select browser-default"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              >
                <option value="">Selecciona marca…</option>
                {brands.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            <div className="MIL-rightNote">
              <span className="MIL-muted">Mostrando: </span>
              <b style={{ marginLeft: 6 }}>{siteLabel(site)}</b>
            </div>

            <button className="MIL-searchBtn" onClick={handleSearch} disabled={loading}>
              {loading ? "BUSCANDO..." : "BUSCAR"}
            </button>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 10,
            }}
          >
            <div className="MIL-muted" style={{ fontSize: 13 }}>
              Filas: <b>{rows.length}</b> {rows.length ? `(mostrando ${visibleRows.length})` : ""}
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                className="MIL-closeBtn"
                onClick={handleLoadQadVisible}
                disabled={loadingQad || loading || !rows.length}
                title="Carga QAD por lotes para las filas visibles (evita timeout)."
              >
                {loadingQad ? "CARGANDO QAD..." : "CARGAR QAD (VISIBLE)"}
              </button>

              {rows.length > visibleRows.length ? (
                <button
                  className="MIL-closeBtn"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={loading || loadingQad}
                >
                  MOSTRAR MÁS
                </button>
              ) : null}
            </div>
          </div>

          <div className="MIL-meta">
            <div>
              <span className="MIL-muted">Total contado: </span>
              <b>{totals.totalCounted}</b>
            </div>
            <div>
              <span className="MIL-muted">Total QAD: </span>
              <b>{totals.hasAllStock ? totals.totalQad : "—"}</b>
            </div>
            <div>
              <span className="MIL-muted">Diferencia: </span>
              <b className={totals.hasAllStock ? pillClass(totals.diff) : ""}>
                {totals.hasAllStock ? totals.diff : "—"}
              </b>
            </div>
          </div>

          {error ? <div className="MIL-error">{error}</div> : null}
          {qadError ? <div className="MIL-error">{qadError}</div> : null}

          <div className="MIL-tableWrap">
            <table className="MIL-table">
              <thead>
                <tr>
                  <th>product_id</th>
                  <th>article</th>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th>Color</th>
                  <th>Talla</th>
                  <th className="MIL-num">Contado</th>
                  <th className="MIL-num">QAD</th>
                  <th className="MIL-num">Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {!visibleRows.length ? (
                  <tr>
                    <td colSpan={9} className="MIL-muted" style={{ padding: 14 }}>
                      {loading ? "Consultando..." : "Sin datos"}
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((r, idx) => {
                    const counted = toInt(r.counted_qty);

                    const pid = String(r.product_id || "").trim();
                    const hasPid = Boolean(pid);

                    const cachedStock = hasPid
                      ? stockByPid[pid]
                      : null;

                    const hasQad = hasPid && Object.prototype.hasOwnProperty.call(stockByPid, pid);
                    const qad = hasQad ? toInt(cachedStock) : 0;

                    // Si no hay QAD aún, no mostramos diferencia real (para evitar confundir)
                    const diff = hasQad ? counted - qad : null;

                    const k = rowKey(r, idx);
                    const selected = selectedKey && selectedKey === k;

                    return (
                      <tr
                        key={k}
                        className={`MIL-rowClickable ${selected ? "MIL-rowSelected" : ""}`}
                        onClick={() => openBreakdown(r, idx)}
                        title="Click para ver desglose (y cargar QAD del producto)"
                      >
                        <td>{r.product_id || ""}</td>
                        <td>{r.article || ""}</td>
                        <td>{r.brand_name || ""}</td>
                        <td>{r.model_name || ""}</td>
                        <td>{r.color_label || ""}</td>
                        <td>{r.size_label || ""}</td>
                        <td className="MIL-num">{counted}</td>
                        <td className="MIL-num">
                          {hasQad ? qad : <span className="MIL-muted">—</span>}
                        </td>
                        <td className="MIL-num">
                          {hasQad ? (
                            <span className={pillClass(diff)}>{diff}</span>
                          ) : (
                            <span className="MIL-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="MIL-detailWrap">
            <div className="MIL-detailHeader">
              <div className="MIL-subTitle">
                Desglose del conteo{" "}
                <span className="MIL-muted">(click en otra fila para cambiar)</span>
              </div>

              {(breakdown || breakdownLoading) ? (
                <button
                  className="MIL-closeBtn"
                  onClick={() => {
                    setBreakdown(null);
                    setSelectedKey("");
                    setBreakdownError("");
                  }}
                >
                  CERRAR DETALLE
                </button>
              ) : null}
            </div>

            {breakdownLoading ? (
              <div className="MIL-muted" style={{ padding: "8px 2px" }}>
                Generando desglose...
              </div>
            ) : breakdownError ? (
              <div className="MIL-error">{breakdownError}</div>
            ) : breakdown ? (
              <>
                <div className="MIL-detailMeta">
                  <div>
                    <span className="MIL-muted">Líneas: </span>
                    <b>{toInt(breakdown?.totals?.lines)}</b>
                  </div>
                  <div>
                    <span className="MIL-muted">Ubicaciones: </span>
                    <b>{toInt(breakdown?.totals?.locations)}</b>
                  </div>
                  <div>
                    <span className="MIL-muted">Total contado: </span>
                    <b>{toInt(breakdown?.totals?.counted)}</b>
                  </div>
                  <div>
                    <span className="MIL-muted">QAD: </span>
                    <b>{toInt(breakdown?.totals?.system_stock)}</b>
                  </div>
                  <div>
                    <span className="MIL-muted">Diferencia: </span>
                    <b className={pillClass(toInt(breakdown?.totals?.difference))}>
                      {toInt(breakdown?.totals?.difference)}
                    </b>
                  </div>
                </div>

                <div className="MIL-subSectionTitle">Detalle (cada registro guardado)</div>
                <div className="MIL-tableWrap">
                  <table className="MIL-table MIL-tableTight">
                    <thead>
                      <tr>
                        <th>Ubicación</th>
                        <th>Rack</th>
                        <th className="MIL-num">Qty</th>
                        <th>Agente</th>
                        <th>Fecha/Hora</th>
                        <th>Notas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(breakdown.lines || []).map((x, i) => (
                        <tr key={`${x.location}-${x.counted_at}-${i}`}>
                          <td>{x.location || ""}</td>
                          <td>{x.rack_code || ""}</td>
                          <td className="MIL-num">{toInt(x.qty)}</td>
                          <td>{x.counted_by_name || ""}</td>
                          <td>{fmtDate(x.counted_at)}</td>
                          <td>{x.notes || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="MIL-muted" style={{ padding: "8px 2px" }}>
                Selecciona una fila para ver el desglose.
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button className="MIL-closeBtn" onClick={() => onClose?.()}>
              CERRAR
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
